import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../context/ThemeContext';
import './ScreenshotLightbox.css';

interface ScreenshotLightboxProps {
  src: string;
  alt: string;
  caption?: string;
  triggerRef: React.RefObject<HTMLButtonElement>;
  onClose: () => void;
}

const ScreenshotLightbox: React.FC<ScreenshotLightboxProps> = ({ src, alt, caption, triggerRef, onClose }) => {
  const { colors, dsColors } = useTheme();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [failed, setFailed] = React.useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    if (!dialog.open) dialog.showModal();
    closeRef.current?.focus();
    const trigger = triggerRef.current;

    const body = document.body;
    const doc = document.documentElement;
    const sx = window.scrollX;
    const sy = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      width: body.style.width,
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
      scrollBehavior: doc.style.scrollBehavior,
    };
    const scrollbar = window.innerWidth - doc.clientWidth;
    body.style.position = 'fixed';
    body.style.top = `-${sy}px`;
    body.style.left = `-${sx}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    if (scrollbar > 0) {
      const existing = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${existing + scrollbar}px`;
    }

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    // A single visible control still needs an explicit trap: the native dialog
    // lets Tab/Shift+Tab escape to the document when the wrap target is missing.
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.hasAttribute('disabled'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dialog.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('keydown', handleKeydown);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('keydown', handleKeydown);
      if (dialog.open) dialog.close();
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      body.style.paddingRight = prev.paddingRight;
      doc.style.scrollBehavior = 'auto';
      window.scrollTo(sx, sy);
      doc.style.scrollBehavior = prev.scrollBehavior;
      trigger?.focus({ preventScroll: true });
    };
    // Mount/teardown runs once per open session; src changes rerender in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) onClose();
  };

  return createPortal(
    <dialog
      ref={dialogRef}
      className="sl-dialog"
      aria-label={caption || alt}
      onClick={onBackdropClick}
      style={{ color: colors.textPrimary }}
    >
      <div className="sl-panel" style={{ backgroundColor: colors.surface }}>
        <button
          ref={closeRef}
          type="button"
          className="sl-close focus:outline-none focus-visible:ring-2"
          aria-label="Close"
          onClick={onClose}
          style={{ color: colors.textPrimary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
        >
          Close
        </button>
        {failed ? (
          <p className="sl-fallback" style={{ color: colors.textSecondary }}>
            The full image could not be loaded.
          </p>
        ) : (
          <img className="sl-image" src={src} alt={alt} onError={() => setFailed(true)} />
        )}
        {caption && (
          <p className="sl-caption text-xs" style={{ color: colors.textSecondary }}>
            {caption}
          </p>
        )}
      </div>
    </dialog>,
    document.body
  );
};

export default ScreenshotLightbox;
